import { defineStore } from 'pinia';
import { BuyerMessageFromBackend, BuyerMessage } from '@buyer/types/BuyerMessage';
import serverFetch from '@globals/ServerFetch';
import { isBuyerApp } from '@/globals/utilities/isMobileApp';

// eslint-disable-next-line import/prefer-default-export
export const useBuyerMessagesStore = defineStore({
	id: 'buyerMessages',
	state: () => ({
		/**
		 * Lista wiadomości.
		 */
		buyerMessagesList: [] as BuyerMessage[],
		/**
		 * Liczba nieprzeczytanych wiadomości.
		 */
		buyerMessagesCount: 0,
		/**
		 * Czy dzwonek jest aktywny.
		 */
		isBellRinging: false,
	}),
	actions: {
		/**
		 * Pobiera listę wiadomości z serwera.
		 */
		async update() {
			const buyerApp = await isBuyerApp();
			const result = [] as Array<BuyerMessage>;
			const messages = (await serverFetch('/buyer/notification/find-all.do', {}, 'GET', false, false)).all_notification_json as BuyerMessageFromBackend[] || [];
			const routeTypes = ['onboarding', 'uncoverRfp', 'message', 'other'];
			let readCount = 0;

			for (const msg of (messages || [])) {
				let msgType;
				switch (msg.id_notif_type) {
				case 1:
					msgType = 'uncoverRfp';
					break;
				case 2:
					msgType = 'message';
					break;
				case 3:
					msgType = 'onboarding';
					break;
				case 7:
					msgType = 'danger';
					break;
				case 9:
					msgType = 'toDoRfps';
					break;
				default:
					msgType = 'other';
					break;
				}

				const messageObject = {
					id: msg.id,
					type: msgType as BuyerMessage['type'],
					title: msg.title,
					message: msg.description,
					date: new Date(msg.ts_created),
					isRead: msg.is_read,
					action: { type: routeTypes.includes(msgType) ? 'route' : 'url', value: msg.redirect_url },
					isFakeNotification: false,
					idRfp: -1,
					idCustomer: -1,
					chatSection: '',
				};

				const { redirect_url } = msg;
				if (redirect_url.startsWith('/moje-zapytania/rfp/')) {
					const urlArr = redirect_url.replace('/moje-zapytania/rfp/', '').split('/');
					messageObject.idRfp = Number(urlArr[0]);
					messageObject.idCustomer = Number(urlArr[1]);
					messageObject.chatSection = urlArr[2];

					if (messageObject.idCustomer) {
						messageObject.action.value = buyerApp ? 'BuyerAppRfpChatCustomerInfo' : 'RfpChatCustomerInfo';
					} else {
						messageObject.action.value = buyerApp ? 'BuyerAppRfpDetails' : 'BuyerRfpDetails';
					}
				}

				result.push(messageObject);				

				if (msg.is_read) { readCount++; }
			}

			this.buyerMessagesList = result;
			this.buyerMessagesCount = result.length - readCount;
		},

		showFakeNotifications(notifications?: BuyerMessage[]) {
			if (!notifications) {
				this.isBellRinging = false;
				return;
			}

			const result = [] as Array<BuyerMessage>;
			let newMessagesInStore = 0;

			for (const msg of notifications) {
				if (this.buyerMessagesList.length > 0) {
					const foundMessage = this.buyerMessagesList.find((m) => m.id === msg.id);
					result.push(foundMessage || msg);
					if (!foundMessage) newMessagesInStore++;
				} else {
					if (window.localStorage.getItem(`is_${msg.type}_notification_read`) !== 'true') newMessagesInStore++;
					msg.isRead = window.localStorage.getItem(`is_${msg.type}_notification_read`) === 'true';
					result.push(msg);
				}
			}

			this.buyerMessagesList = result;
			this.buyerMessagesCount = result.filter((m) => !m.isRead).length;

			if (newMessagesInStore > 0) {
				this.isBellRinging = true;
			}
		},

		/**
		 * Oznacza wszystkie wiadomości jako przeczytane.
		 */
		async markAllAsRead() {
			this.buyerMessagesCount = 0;
			this.buyerMessagesList.forEach((msg) => {
				msg.isRead = true;
			});

			await fetch('/buyer/notification/set-all-is-read-true.do', {
				method: 'PUT',
			});
		},

		/**
		 * Oznacza wiadomość jako przeczytaną.
		 */
		async markAsRead(message:BuyerMessage) {
			const { id } = message;
			const foundMessage = this.buyerMessagesList.find((m) => m.id === id);
			if (foundMessage && foundMessage.isFakeNotification) {
				foundMessage.isRead = true;
				this.buyerMessagesCount--;
				window.localStorage.setItem(`is_${foundMessage.type}_notification_read`, 'true');
				return;
			}
			if (foundMessage && !foundMessage.isRead) {
				foundMessage.isRead = true;
				this.buyerMessagesCount--;

				await fetch(`/buyer/notification/set-is-read-true/${id}.do`, {
					method: 'PUT',
				});
			}
		},
		async checkIfBellIsRinging() {
			await fetch('/buyer/notification/is-bell-ringing.do').then((response) => {
				if (response.ok) {
					response.json().then((data) => {
						this.isBellRinging = data;
					});
				}
			});
		},
		async unringTheBell() {
			await fetch('/buyer/notification/set-is-ringing-bell-false.do', {
				method: 'PUT',
			}).then(() => {
				this.isBellRinging = false;
			});
		},
		ringTheBell() {
			this.isBellRinging = true;
		},
	},
});
